'use client';

import React, {FC, useEffect, useState} from 'react';
import {Collapse, CollapseProps} from 'react-collapse';
import {createGlobalStyle} from 'styled-components';

const collapseClassName = 'MeshExpander--collapse';
const quarticEaseOut = 'cubic-bezier(.17, .84, .44, 1)';

const CollapseStyle = createGlobalStyle`
  .${collapseClassName} {
    transition: height 300ms;
    transition-timing-function: ${quarticEaseOut};
  }
`;

export interface ExpanderProps extends Omit<CollapseProps, 'theme' | 'isOpened' | 'aria-hidden' | 'hidden' | 'onRest' | 'children'> {
  onExpand?: () => void;
  onCollapse?: () => void;
  onBeforeExpand?: () => void;
  onBeforeCollapse?: () => void;
  expanded?: boolean;
  children: React.ReactNode;
}

type UseExpanderLifecycleProps = Pick<ExpanderProps, 'expanded' | 'onExpand' | 'onCollapse' | 'onBeforeExpand' | 'onBeforeCollapse'>;

function useExpanderLifecycle({expanded, onExpand, onCollapse, onBeforeExpand, onBeforeCollapse}: UseExpanderLifecycleProps): void {
  const [prevExpanded, setPrevExpanded] = useState(expanded);

  if (expanded && !prevExpanded) {
    onBeforeExpand?.();
    setPrevExpanded(expanded);
  }

  if (!expanded && prevExpanded) {
    onBeforeCollapse?.();
    setPrevExpanded(expanded);
  }

  useEffect(() => {
    expanded ? onExpand?.() : onCollapse?.();
  }, [expanded]);
}

const Expander: FC<ExpanderProps> = ({onExpand, onCollapse, onBeforeExpand, onBeforeCollapse, expanded = false, className, children, ...otherProps}) => {
  const [shouldHideChildren, setHideChildren] = useState(!expanded);

  function handleRest() {
    if (!expanded) {
      setHideChildren(true);
    }
  }

  useExpanderLifecycle({onExpand, onCollapse, onBeforeExpand, onBeforeCollapse, expanded});

  useEffect(() => {
    if (expanded) {
      setHideChildren(false);
    }
  }, [expanded]);

  return (
    <>
      <CollapseStyle />

      {/* Collapse does not accept unknown props so we have to wrap in a div with a conditional hidden attribute */}
      <div hidden={!expanded && shouldHideChildren} data-mesh-component="EXPANDER">
        <Collapse {...otherProps} theme={{collapse: collapseClassName}} isOpened={expanded} aria-hidden={!expanded} onRest={handleRest} className={className}>
          {children}
        </Collapse>
      </div>
    </>
  );
};

export default Expander;
